import React, { useEffect, useState } from 'react';
import { Edit3, Check } from 'lucide-react';

export interface PayrollFeedbackIssues {
  manualNewHire: boolean;
  manualTermination: boolean;
  salaryBlockedInCore: boolean;
  deductionsMissed: boolean;
  hoursOrAttachmentsIncorrect: boolean;
  payAgentHallucinated: boolean;
  entireProcessFailed: boolean;
}

export interface PayrollFeedback {
  issues: PayrollFeedbackIssues;
  additionalDetails: string;
}

interface PayrollFeedbackModalProps {
  isOpen: boolean;
  onSkip: () => void;
  onSubmit: (feedback: PayrollFeedback) => void;
}

export interface IssueOption {
  key: keyof PayrollFeedbackIssues;
  label: string;
}

// Single source of truth for the seven issue checkboxes — mirrors
// PAYROLL_FEEDBACK_ISSUE_FIELDS in backend/schemas/cases.py.
export const ISSUE_OPTIONS: IssueOption[] = [
  { key: 'manualNewHire', label: 'New hire had to be manually added' },
  { key: 'manualTermination', label: 'Termination had to be manually entered' },
  { key: 'salaryBlockedInCore', label: 'Salary employee had to be blocked in Core' },
  { key: 'deductionsMissed', label: 'Deductions were missed (meal, advances, etc.)' },
  {
    key: 'hoursOrAttachmentsIncorrect',
    label: "Hours or attachments didn't pull in correctly",
  },
  {
    key: 'payAgentHallucinated',
    label: 'Pay Agent hallucinated (wrong columns, incorrect amounts)',
  },
  { key: 'entireProcessFailed', label: 'The entire process did not work' },
];

const EMPTY_ISSUES: PayrollFeedbackIssues = {
  manualNewHire: false,
  manualTermination: false,
  salaryBlockedInCore: false,
  deductionsMissed: false,
  hoursOrAttachmentsIncorrect: false,
  payAgentHallucinated: false,
  entireProcessFailed: false,
};

const PayrollFeedbackModal: React.FC<PayrollFeedbackModalProps> = ({
  isOpen,
  onSkip,
  onSubmit,
}) => {
  const [issues, setIssues] = useState<PayrollFeedbackIssues>(EMPTY_ISSUES);
  const [additionalDetails, setAdditionalDetails] = useState('');

  useEffect(() => {
    if (isOpen) {
      setIssues(EMPTY_ISSUES);
      setAdditionalDetails('');
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const toggleIssue = (key: keyof PayrollFeedbackIssues) => {
    setIssues((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = () => {
    onSubmit({ issues, additionalDetails: additionalDetails.trim() });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-lg mx-4 shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center space-x-3 px-6 pt-6 pb-4">
          <div className="w-10 h-10 bg-cyan-50 rounded-full flex items-center justify-center flex-shrink-0">
            <Edit3 className="w-5 h-5 text-cyan-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 leading-tight">
              Before We Process
            </h3>
            <p className="text-sm text-neutral-600">
              Help us improve the Payroll Agent
            </p>
          </div>
        </div>

        <div className="px-6 pb-4 overflow-y-auto">
          <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-4">
            <p className="text-sm text-cyan-900 mb-3">
              Did anything require manual intervention? This feedback helps us measure
              and improve the Payroll Agent.
            </p>
            <p className="text-sm font-semibold text-cyan-900 mb-2">
              Check any issues you encountered:
            </p>

            <div className="space-y-1">
              {ISSUE_OPTIONS.map((option) => (
                <label
                  key={option.key}
                  className="flex items-start space-x-3 py-1.5 cursor-pointer group"
                >
                  <input
                    type="checkbox"
                    checked={issues[option.key]}
                    onChange={() => toggleIssue(option.key)}
                    className="mt-0.5 w-4 h-4 rounded border-cyan-300 text-cyan-700 focus:ring-cyan-500 cursor-pointer"
                  />
                  <span className="text-sm text-neutral-800 group-hover:text-neutral-900">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>

            <div className="border-t border-cyan-100 mt-4 pt-4">
              <label
                htmlFor="payroll-feedback-details"
                className="block text-sm font-semibold text-cyan-900 mb-2"
              >
                Additional details{' '}
                <span className="font-normal text-neutral-500">(optional)</span>
              </label>
              <textarea
                id="payroll-feedback-details"
                value={additionalDetails}
                onChange={(e) => setAdditionalDetails(e.target.value)}
                rows={3}
                placeholder="Provide any additional context, like specific employee names or what you had to correct..."
                className="w-full border border-cyan-300 rounded-lg p-3 text-sm placeholder-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-neutral-100">
          <button
            type="button"
            onClick={onSkip}
            className="px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors text-sm font-medium"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 bg-cyan-700 text-white rounded-lg hover:bg-cyan-800 transition-colors text-sm font-semibold flex items-center space-x-2"
          >
            <Check size={16} />
            <span>Save &amp; Process</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayrollFeedbackModal;
